import React, { useEffect, useMemo, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Link } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { adminApi, publicAssetUrl } from '../../lib/adminApi';

const typeLabels = {
  department: 'Department',
  center: 'Center',
  directorate: 'Directorate',
  unit: 'Unit',
};

const statusClass = {
  active: 'bg-emerald-100 text-emerald-700',
  draft: 'bg-amber-100 text-amber-700',
  inactive: 'bg-slate-100 text-slate-700',
};

const adminFade = {
  hidden: { opacity: 0, y: 18 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.42, ease: [0.22, 1, 0.36, 1] } },
};

const adminStagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.045 } },
};

export default function AdminInstitutionalPageList({ fixedType = '' }) {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [type, setType] = useState(fixedType || '');
  const [status, setStatus] = useState('');
  const reduceMotion = useReducedMotion();

  const title = useMemo(() => {
    if (fixedType) return `${typeLabels[fixedType]} CMS`;
    return 'Institutional Pages CMS';
  }, [fixedType]);

  async function loadPages() {
    try {
      setLoading(true);
      setError('');
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      if (type) params.set('type', type);
      if (status) params.set('status', status);
      const data = await adminApi(`/api/admin/institutional-pages?${params.toString()}`);
      setPages(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadPages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, status, fixedType]);

  async function changeStatus(id, nextStatus) {
    try {
      await adminApi(`/api/admin/institutional-pages/${id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status: nextStatus }),
      });
      await loadPages();
    } catch (err) {
      alert(err.message);
    }
  }

  async function deletePage(id) {
    if (!window.confirm('Delete this institutional page permanently?')) return;
    try {
      await adminApi(`/api/admin/institutional-pages/${id}`, { method: 'DELETE' });
      setPages((items) => items.filter((item) => item.id !== id));
    } catch (err) {
      alert(err.message);
    }
  }

  return (
    <AdminLayout
      title={title}
      subtitle="Create and manage departments, centers, directorates and units using one consistent UI/UX template."
      actions={<Link to={`/adxm/institutional-pages/create${fixedType ? `?type=${fixedType}` : ''}`} className="rounded-xl bg-emerald-700 px-4 py-3 text-sm font-black text-white shadow hover:bg-emerald-800">+ Add Page</Link>}
    >
      <motion.div variants={adminFade} initial="hidden" animate="visible" className="mb-6 grid gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm lg:grid-cols-[1fr_190px_190px_auto]">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, group, head..." className="rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-emerald-600" />
        {!fixedType && (
          <select value={type} onChange={(e) => setType(e.target.value)} className="rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-emerald-600">
            <option value="">All types</option>
            <option value="department">Departments</option>
            <option value="center">Centers</option>
            <option value="directorate">Directorates</option>
            <option value="unit">Units</option>
          </select>
        )}
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-emerald-600">
          <option value="">All status</option>
          <option value="draft">Draft</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
        <button onClick={loadPages} className="rounded-xl border border-emerald-200 px-5 py-3 text-sm font-black text-emerald-700 hover:bg-emerald-50">Search</button>
      </motion.div>

      {error && <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-4 text-sm font-bold text-red-700">{error}</div>}
      {loading ? <div className="rounded-xl bg-white p-6 text-sm font-bold text-slate-600 shadow-sm">Loading institutional pages...</div> : (
        <motion.div variants={adminFade} initial="hidden" animate="visible" className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50 text-left text-xs font-black uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-5 py-4">Page</th>
                  <th className="px-5 py-4">Type</th>
                  <th className="px-5 py-4">Head</th>
                  <th className="px-5 py-4">Status</th>
                  <th className="px-5 py-4 text-right">Action</th>
                </tr>
              </thead>
              <motion.tbody variants={adminStagger} initial="hidden" animate="visible" className="divide-y divide-slate-100">
                {pages.map((item) => (
                  <motion.tr key={item.id} variants={adminFade} whileHover={reduceMotion ? undefined : { x: 3 }} className="align-top hover:bg-slate-50">
                    <td className="px-5 py-4">
                      <div className="flex gap-4">
                        <div className="h-16 w-20 flex-none overflow-hidden rounded-xl bg-slate-100">
                          {item.hero_image ? <img src={publicAssetUrl(item.hero_image)} alt="" className="h-full w-full object-cover" /> : null}
                        </div>
                        <div>
                          <div className="font-black text-slate-950">{item.name}</div>
                          <div className="mt-1 line-clamp-2 max-w-xl text-xs text-slate-600">{item.short_description}</div>
                          {item.public_url && <Link to={item.public_url} className="mt-2 inline-block text-xs font-black text-emerald-700">View public page</Link>}
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-slate-600">{typeLabels[item.page_type] || item.page_type}<br /><span className="text-xs text-slate-400">{item.parent_group || '-'}</span></td>
                    <td className="px-5 py-4 text-slate-600">{item.head_name || '-'}</td>
                    <td className="px-5 py-4"><span className={`rounded-full px-3 py-1 text-xs font-black ${statusClass[item.status] || statusClass.inactive}`}>{item.status}</span></td>
                    <td className="px-5 py-4">
                      <div className="flex flex-wrap justify-end gap-2">
                        <Link to={`/adxm/institutional-pages/edit/${item.id}`} className="rounded-lg border border-slate-200 px-3 py-2 font-bold text-slate-700 hover:bg-slate-100">Edit</Link>
                        {item.status !== 'active' ? <button onClick={() => changeStatus(item.id, 'active')} className="rounded-lg border border-emerald-200 px-3 py-2 font-bold text-emerald-700 hover:bg-emerald-50">Publish</button> : <button onClick={() => changeStatus(item.id, 'inactive')} className="rounded-lg border border-amber-200 px-3 py-2 font-bold text-amber-700 hover:bg-amber-50">Unpublish</button>}
                        <button onClick={() => deletePage(item.id)} className="rounded-lg border border-red-200 px-3 py-2 font-bold text-red-700 hover:bg-red-50">Delete</button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
                {!pages.length && <tr><td colSpan="5" className="px-5 py-10 text-center text-sm font-bold text-slate-500">No institutional pages found.</td></tr>}
              </motion.tbody>
            </table>
          </div>
        </motion.div>
      )}
    </AdminLayout>
  );
}
